import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { SectionTitle, Text } from 'components/typography'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Map from '../components/map/map'
import ListFiledBuildingName from '../features/contracts/list/list-filed-building-name'
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
} from '../pages/contract/components/accordion'
import { convertPrice } from '../utils'
import { HeaderHeightPx } from './header'

interface BasePropertyItem {
  id: string
  url: string
  name: string
  address: string
  salesPrice: number
  builtDate: string
  floorPlan: string
  unitSize: number
  story: number
  type: string
  lat?: string
  lon?: string
  itemNumberLabel?: string
}

interface DisplayConfig {
  address?: string
  square?: number
  buildYearMonth?: string
}

interface Position {
  lat: number | null
  lon: number | null
}

interface PropertyMapViewProps<T extends BasePropertyItem> {
  pageTitle: string
  sectionTitle: string
  items: T[]
  position: Position | null
  displayConfig: DisplayConfig | null
  visibleItems: T[]
  currentVisibleItem: T | null
  selectedItemId?: string
  isMobile: boolean
  onItemClick: (item: T) => void
  renderList: (props: {
    items: T[]
    currentVisibleItem: T | null
    isMobile: boolean
    selectedItemId?: string
    onItemClick: (item: T) => void
  }) => ReactNode
  getAccordionTitle: (hasConditions: boolean) => string
  ownerNotice?: string
  currentPanelIndex?: number
  onPanelIndexChange?: (index: number) => void
}

const PropertyMapWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  height: 100vh;
  position: relative;

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    grid-template-columns: minmax(320px, 33vw) minmax(520px, 800px);
    grid-template-areas: 'map list';
    justify-content: start;
    height: calc(100vh - ${HeaderHeightPx});
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    grid-template-rows: 1fr auto;
    grid-template-areas:
      'map'
      'list';
    gap: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
`

const FullWidthSection = styled.div`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
`

const MapArea = styled.div`
  width: 100%;
  height: 600px;
  position: relative;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    grid-area: map;
    height: 100%;
    max-height: none;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    grid-area: map;
    height: calc(100vh - ${HeaderHeightPx});
    width: 100vw;
    position: fixed;
    top: ${HeaderHeightPx};
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    overflow: hidden;
    margin: 0;
    padding: 0;
  }
`

const MapWrapper = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  position: relative;
`

const SelectedPanelArea = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  padding: ${(p) => p.theme.space.s} ${(p) => p.theme.space.m};
  padding-top: 0;
  max-height: 50vh;
  overflow-y: auto;
  box-sizing: border-box;
  width: min(640px, 100%);
  margin: 0;
  user-select: none;
  display: flex;
  justify-content: center;
  border: 1px solid ${(p) => p.theme.BORDER};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    display: none;
  }
`

const ListArea = styled.div`
  width: 100%;
  padding: ${(p) => p.theme.space.xxl} ${(p) => p.theme.space.l};

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    grid-area: list;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    grid-area: list;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    padding: ${(p) => p.theme.space.s} ${(p) => p.theme.space.m};
    padding-top: 0;
    max-height: 50vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
    width: 100%;
    margin: 0;
    touch-action: pan-y;
  }
`

const ListWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  width: 100%;
  justify-items: center;

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    width: 100%;
    overflow: scroll;
    height: calc(100vh - 240px);
    max-height: calc(100vh - 240px);
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    grid-template-columns: 1fr;
    justify-items: stretch;
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
`

const Bold = styled.div`
  font-weight: 700;
`

const SalesPrice = styled(Bold)`
  font-size: 12px;
  font-weight: 700;
`

const BuildingDetailArea = styled.div`
  margin-top: 7px;
`

const BuildingDetail = styled.div``

const MobileSectionLayout = styled.div`
  margin-bottom: ${(p) => p.theme.space.xxl};
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    display: none;
  }
`
const MobileSectionTitle = styled(SectionTitle)`
  font-size: ${(p) => p.theme.font.size.xxxl};
`

const MobileText = styled(Text)`
  padding-bottom: ${(p) => p.theme.space.m};
`

// const MobilePanelHeader = styled.div`
//   display: none;

//   @media only screen and (max-width: ${(p) => p.theme.vp}px) {
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     margin-bottom: 16px;
//     padding-bottom: 8px;
//     width: 100%;
//     box-sizing: border-box;
//   }
// `

// const MobilePanelCounter = styled.div`
//   font-size: 14px;
//   color: #6b7280;
//   font-weight: 500;
// `

// const MobilePanelNavigation = styled.div`
//   display: flex;
//   gap: 8px;
// `

// const MobilePanelButton = styled.button<{ disabled?: boolean }>`
//   background: ${(p) => (p.disabled ? '#f3f4f6' : p.theme.MAIN)};
//   color: ${(p) => (p.disabled ? '#9ca3af' : 'white')};
//   border: none;
//   border-radius: 6px;
//   width: 32px;
//   height: 32px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
//   font-size: 14px;
//   font-weight: bold;
//   transition: all 0.2s ease;

//   &:hover:not(:disabled) {
//     opacity: 0.8;
//   }
// `

const AccordionWrapper = styled.div`
  position: absolute;
  top: 12px;
  left: 60px;
  z-index: 2;
  max-width: 300px;
  pointer-events: auto;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    top: 12px;
    left: 60px;
    transform: none;
    right: auto;
    max-width: 298px;
    width: auto;
    z-index: 2;
  }
`

const ConditionItem = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  &:last-child {
    margin-bottom: 0;
  }
`

const ConditionLabel = styled.span`
  margin-right: 8px;
  flex-shrink: 0;
  font-size: ${(p) => p.theme.font.size.m};
`

const ConditionValue = styled.span`
  font-weight: 600;
  color: #374151;
  word-break: break-all;
  line-height: 1.5;
  font-size: ${(p) => p.theme.font.size.m};
`

const NoticeText = styled(Text)<{ $hasConditions?: boolean }>`
  margin-top: ${(p) => (p.$hasConditions ? p.theme.space.l : '0')};
  font-size: ${(p) => p.theme.font.size.xs};
  line-height: 1.5;
`

const AccordionTitle = styled.div`
  font-size: ${(p) => p.theme.font.size.m};
  font-weight: 600;
  color: #374151;
`

const SwipeGuideModal = styled.div<{
  $isMobile: boolean
  $isMounted: boolean
  $hasItem: boolean
}>`
  display: ${(p) =>
    p.$isMobile && p.$isMounted && p.$hasItem ? 'flex' : 'none'};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 224px;
  background-color: rgba(38, 38, 38, 0.9);
  z-index: 9999;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  border-radius: 8px;
  pointer-events: none;
  animation: ${(p) =>
    p.$isMobile && p.$isMounted && p.$hasItem
      ? 'fadeInOut 2.5s ease-in-out forwards'
      : 'none'};

  @keyframes fadeInOut {
    0% {
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      display: none;
    }
  }
`

const SwipeGuideIcon = styled.img`
  width: 64px;
  height: 64px;
`

const SwipeGuideText = styled.div`
  color: white;
  font-size: 16px;
  text-align: center;
  line-height: 1.6;
  white-space: pre-line;
`

function PropertyMapView<T extends BasePropertyItem>({
  sectionTitle,
  items,
  position,
  displayConfig,
  visibleItems,
  currentVisibleItem,
  selectedItemId,
  isMobile,
  onItemClick,
  renderList,
  getAccordionTitle,
  ownerNotice,
  currentPanelIndex: externalPanelIndex,
  onPanelIndexChange,
}: PropertyMapViewProps<T>) {
  const [internalPanelIndex, setInternalPanelIndex] = useState<number>(0)
  const [panelHeight, setPanelHeight] = useState<number>(0)
  const [startX, setStartX] = useState<number>(0)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isAccordionExpanded, setIsAccordionExpanded] = useState<boolean>(false)
  const [isMounted, setIsMounted] = useState<boolean>(false)

  const currentPanelIndex = externalPanelIndex ?? internalPanelIndex
  const setCurrentPanelIndex = (index: number) => {
    if (onPanelIndexChange) {
      onPanelIndexChange(index)
    } else {
      setInternalPanelIndex(index)
    }
  }

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 1200

      if (mobile) {
        const viewportHeight = window.innerHeight
        const calculatedPanelHeight = viewportHeight * 0.5
        setPanelHeight(calculatedPanelHeight)
      } else {
        setPanelHeight(0)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  useEffect(() => {
    // マウント時のフラグのみ設定（モーダル表示用）
    setIsMounted(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formatYM = (ym: string) => {
    return `${ym.slice(0, 4)}/${ym.slice(5, 7)}`
  }

  const hasDisplayConditions = useMemo(() => {
    return !!(
      displayConfig?.address ||
      displayConfig?.square ||
      displayConfig?.buildYearMonth
    )
  }, [displayConfig])

  const { selectedCenter, selectedZoom, currentSelectedId } = useMemo(() => {
    if (currentVisibleItem && isMobile) {
      const itemIndex = visibleItems.findIndex(
        (i) => i.id === currentVisibleItem.id
      )
      return {
        selectedCenter: [
          Number(currentVisibleItem.lat),
          Number(currentVisibleItem.lon),
        ] as [number, number],
        selectedZoom: 18,
        currentSelectedId: `marker-${itemIndex}`,
      }
    }

    if (selectedItemId) {
      const markerIndex = parseInt(selectedItemId.replace('marker-', ''))
      const selectedItem = visibleItems[markerIndex]

      if (selectedItem?.lat && selectedItem?.lon) {
        return {
          selectedCenter: [
            Number(selectedItem.lat),
            Number(selectedItem.lon),
          ] as [number, number],
          selectedZoom: 18,
          currentSelectedId: selectedItemId,
        }
      }
    }

    return {
      selectedCenter: undefined,
      selectedZoom: undefined,
      currentSelectedId: null,
    }
  }, [selectedItemId, visibleItems, currentVisibleItem, isMobile])

  const selectedItemForPanel = useMemo(() => {
    if (!selectedItemId) return null
    const markerIndex = parseInt(selectedItemId.replace('marker-', ''))
    if (Number.isNaN(markerIndex)) return null
    return visibleItems[markerIndex] ?? null
  }, [selectedItemId, visibleItems])

  const handlePrevPanel = () => {
    if (currentPanelIndex > 0) {
      setCurrentPanelIndex(currentPanelIndex - 1)
    }
  }

  const handleNextPanel = () => {
    if (currentPanelIndex < visibleItems.length - 1) {
      setCurrentPanelIndex(currentPanelIndex + 1)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || e.touches.length === 0) return
    const firstTouch = e.touches[0]
    if (firstTouch) {
      setStartX(firstTouch.clientX)
      setIsDragging(true)
    }
  }

  const handleTouchMove = (_e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging || e.changedTouches.length === 0) return

    const firstChangedTouch = e.changedTouches[0]
    if (firstChangedTouch) {
      const endX = firstChangedTouch.clientX
      const diffX = startX - endX
      const threshold = 50

      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          handleNextPanel()
        } else {
          handlePrevPanel()
        }
      }
    }

    setIsDragging(false)
    setStartX(0)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isMobile) return
    setStartX(e.clientX)
    setIsDragging(true)
  }

  const handleMouseMove = (_e: React.MouseEvent) => {
    if (!isMobile || !isDragging) return
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isMobile || !isDragging) return

    const endX = e.clientX
    const diffX = startX - endX
    const threshold = 50

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        handleNextPanel()
      } else {
        handlePrevPanel()
      }
    }

    setIsDragging(false)
    setStartX(0)
  }

  return (
    <FullWidthSection>
      <PropertyMapWrapper>
        <ListArea
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <SwipeGuideModal
            $isMobile={isMobile}
            $isMounted={isMounted}
            $hasItem={visibleItems.length > 0 && currentVisibleItem !== null}
          >
            <SwipeGuideIcon src="/image/swipe.svg" alt="スワイプ" />
            <SwipeGuideText>
              左右スワイプで
              {'\n'}
              物件表示を切り替えます
            </SwipeGuideText>
          </SwipeGuideModal>
          <MobileSectionLayout>
            <MobileSectionTitle>{sectionTitle}</MobileSectionTitle>
            {ownerNotice && <MobileText size={'sm'}>{ownerNotice}</MobileText>}
          </MobileSectionLayout>

          <ListWrapper>
            {renderList({
              items,
              currentVisibleItem,
              isMobile,
              selectedItemId: currentSelectedId || undefined,
              onItemClick,
            })}
          </ListWrapper>
        </ListArea>
        <MapArea>
          <AccordionWrapper>
            <StyledAccordion
              expanded={isAccordionExpanded}
              onChange={(_, expanded) => setIsAccordionExpanded(expanded)}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="display-conditions-content"
                id="display-conditions-header"
              >
                <AccordionTitle>
                  {getAccordionTitle(hasDisplayConditions)}
                </AccordionTitle>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                {displayConfig?.address && (
                  <ConditionItem>
                    <ConditionLabel>住所:</ConditionLabel>
                    <ConditionValue>{displayConfig.address}</ConditionValue>
                  </ConditionItem>
                )}
                {displayConfig?.square && (
                  <ConditionItem>
                    <ConditionLabel>面積:</ConditionLabel>
                    <ConditionValue>{displayConfig.square} m²</ConditionValue>
                  </ConditionItem>
                )}
                {displayConfig?.buildYearMonth && (
                  <ConditionItem>
                    <ConditionLabel>築年月:</ConditionLabel>
                    <ConditionValue>
                      {displayConfig.buildYearMonth}
                    </ConditionValue>
                  </ConditionItem>
                )}
                <NoticeText size={'sm'} $hasConditions={hasDisplayConditions}>
                  ※地図上に表示される物件の位置は付近住所に所在することを表すものであり、実際の物件所在地とは異なる場合がございます。
                  <br />
                  ※地図の更新タイミングの関係で、物件情報が実際のものとは異なる場合や最新情報に更新されていない場合がございます。
                </NoticeText>
              </StyledAccordionDetails>
            </StyledAccordion>
          </AccordionWrapper>
          <MapWrapper>
            <Map
              position={
                position && position.lat !== null && position.lon !== null
                  ? [position.lat, position.lon]
                  : undefined
              }
              markers={visibleItems.map((item) => {
                return {
                  markerItemNumberLabel: item.itemNumberLabel,
                  lat: Number(item.lat),
                  lon: Number(item.lon),
                  popup: isMobile ? undefined : (
                    <>
                      <ListFiledBuildingName name={item.name} url={item.url} />
                      <SalesPrice>
                        {convertPrice(
                          item.salesPrice !== null &&
                            item.salesPrice !== undefined
                            ? item.salesPrice
                            : undefined
                        )}
                      </SalesPrice>
                      <BuildingDetailArea>
                        <BuildingDetail>
                          {item.unitSize}m2、{item.floorPlan}、{item.story}階
                        </BuildingDetail>
                        <BuildingDetail>{item.type}</BuildingDetail>
                        <BuildingDetail>
                          築年月：{formatYM(item.builtDate || '')}
                        </BuildingDetail>
                      </BuildingDetailArea>
                    </>
                  ),
                }
              })}
              selectedItemId={currentSelectedId || undefined}
              selectedCenter={selectedCenter}
              selectedZoom={selectedZoom}
              isMobile={isMobile}
              panelHeight={panelHeight}
              onMarkerClick={(markerIndex) => {
                const item = visibleItems[markerIndex]
                if (item) {
                  onItemClick(item)
                }
              }}
            />
          </MapWrapper>
          {!isMobile && selectedItemForPanel && (
            <SelectedPanelArea>
              {renderList({
                items,
                currentVisibleItem: selectedItemForPanel,
                isMobile: true,
                selectedItemId: currentSelectedId || undefined,
                onItemClick,
              })}
            </SelectedPanelArea>
          )}
        </MapArea>
      </PropertyMapWrapper>
    </FullWidthSection>
  )
}

export default PropertyMapView
